import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { firmasAPI } from '../../api';
import { toast } from '../../hooks/use-toast';

export const COLORES_CASILLA = [
  { borde: 'border-indigo-500', fondo: 'bg-indigo-500/10', texto: 'text-indigo-700', punto: 'bg-indigo-500' },
  { borde: 'border-emerald-600', fondo: 'bg-emerald-500/10', texto: 'text-emerald-700', punto: 'bg-emerald-600' },
  { borde: 'border-rose-500', fondo: 'bg-rose-500/10', texto: 'text-rose-700', punto: 'bg-rose-500' },
  { borde: 'border-amber-500', fondo: 'bg-amber-500/10', texto: 'text-amber-700', punto: 'bg-amber-500' },
  { borde: 'border-sky-600', fondo: 'bg-sky-500/10', texto: 'text-sky-700', punto: 'bg-sky-600' },
  { borde: 'border-fuchsia-600', fondo: 'bg-fuchsia-500/10', texto: 'text-fuchsia-700', punto: 'bg-fuchsia-600' },
];

const ANCHO = 0.34;
const ALTO = 0.075;

// v135: un firmante puede tener varios puntos de firma en el mismo documento.
export const puntosDe = (f) => (f?.casillas?.length
  ? f.casillas
  : (f?.rect_rel?.length ? [{ pagina: f.pagina, rect_rel: f.rect_rel }] : []));

// Diálogo para colocar a mano las casillas de firma de cada firmante sobre el documento.
const ColocarCasillasDialog = ({ open, onOpenChange, adjuntoId, firmantes, maxPaginas,
                                 clientePreviewId, onColocar, onQuitar }) => {
  const [preview, setPreview] = useState(null);
  const [cargadoPara, setCargadoPara] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [activo, setActivo] = useState(null);

  // La hoja de firma que el servidor añade al final lleva el nombre y el DNI del
  // firmante: hay que decirle SIEMPRE de quién es, o usaría el cliente por defecto.
  const cargar = useCallback(async () => {
    const clave = `${adjuntoId}:${clientePreviewId || ''}`;
    if (!open || !adjuntoId || !clientePreviewId || cargadoPara === clave || cargando) return;
    setCargando(true);
    try {
      const res = await firmasAPI.previsualizar(adjuntoId, clientePreviewId);
      setPreview(res.data);
      setCargadoPara(clave);
    } catch (e) {
      toast({ title: 'No se pudo abrir la previsualización',
              description: e?.response?.data?.detail || 'Error inesperado', variant: 'destructive' });
      onOpenChange(false);
    } finally {
      setCargando(false);
    }
  }, [open, adjuntoId, clientePreviewId, cargadoPara, cargando, onOpenChange]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (open && firmantes.length) {
      const sinCasilla = firmantes.find((f) => !puntosDe(f).length);
      setActivo(String((sinCasilla || firmantes[0]).cliente_id));
    }
  }, [open, firmantes]);

  const paginas = (preview?.paginas || []).slice(0, maxPaginas || undefined);
  const nombrePreview = firmantes.find((f) => f.cliente_id === clientePreviewId)?.nombre;
  const hojaAnadida = !!(preview?.ancla?.pagina_anadida || preview?.ancla?.requiere_pagina_nueva)
    && paginas.length === (preview?.paginas || []).length;

  const colocar = (e, indice) => {
    if (!activo) return;
    const caja = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - caja.left) / caja.width;
    const relY = (e.clientY - caja.top) / caja.height;
    const x0 = Math.min(Math.max(relX - ANCHO / 2, 0.02), 0.98 - ANCHO);
    const y0 = Math.min(Math.max(relY - ALTO / 2, 0.02), 0.98 - ALTO);
    const activoFirmante = firmantes.find((f) => String(f.cliente_id) === activo);
    const primero = !puntosDe(activoFirmante).length;
    onColocar(Number(activo), { pagina: indice, rect_rel: [x0, y0, x0 + ANCHO, y0 + ALTO] });
    // Solo se salta al siguiente firmante cuando acaba de colocar su PRIMER punto:
    // así se pueden seguir añadiendo puntos del mismo firmante sin cambiar de pestaña.
    if (primero) {
      const siguiente = firmantes.find((f) => String(f.cliente_id) !== activo && !puntosDe(f).length);
      if (siguiente) setActivo(String(siguiente.cliente_id));
    }
  };

  const faltan = firmantes.filter((f) => !puntosDe(f).length);
  const totalPuntos = firmantes.reduce((n, f) => n + puntosDe(f).length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="colocar-casillas-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Colocar los puntos de firma</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Elige un firmante y haz clic en el documento donde debe firmar. Puedes marcar
            <strong> varios puntos del mismo firmante</strong> (una firma por cláusula, por ejemplo):
            haz clic tantas veces como haga falta. Para quitar un punto, pulsa la «×» de su casilla.
            Las casillas de firmantes distintos no pueden solaparse.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid="casillas-firmantes-tabs">
          {firmantes.map((f, i) => {
            const color = COLORES_CASILLA[i % COLORES_CASILLA.length];
            const sel = String(f.cliente_id) === activo;
            const puntos = puntosDe(f);
            return (
              <button
                key={f.cliente_id}
                type="button"
                onClick={() => setActivo(String(f.cliente_id))}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  sel ? `${color.borde} ${color.fondo} font-semibold` : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                data-testid={`casilla-tab-${f.cliente_id}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${color.punto}`} />
                {f.nombre}
                <span className={puntos.length ? 'text-green-700' : 'text-amber-700'}
                      data-testid={`casilla-tab-estado-${f.cliente_id}`}>
                  {puntos.length === 0
                    ? '· sin colocar'
                    : (puntos.length === 1 ? `· pág. ${puntos[0].pagina + 1}` : `· ${puntos.length} puntos`)}
                </span>
              </button>
            );
          })}
        </div>

        {hojaAnadida && (
          <div className="rounded-md bg-amber-50 text-amber-900 text-xs p-2.5"
               data-testid="casillas-hoja-anadida">
            El documento no tenía sitio libre al final, así que la <strong>última
            página</strong> de esta vista es la <strong>hoja de firma</strong> que se
            añadirá, con los datos de <strong>{nombrePreview}</strong>.
          </div>
        )}

        {cargando && (
          <div className="py-10 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Generando la vista del documento…
          </div>
        )}

        {!cargando && paginas.length > 0 && (
          <div className="space-y-3">
            {paginas.map((p) => (
              <div key={p.indice}
                   className="relative bg-white border rounded overflow-hidden cursor-crosshair"
                   onClick={(e) => colocar(e, p.indice)}
                   data-testid={`casillas-pagina-${p.indice}`}>
                <img src={p.imagen} alt={`Página ${p.indice + 1}`} className="w-full block" />
                {firmantes.map((f, i) => {
                  const color = COLORES_CASILLA[i % COLORES_CASILLA.length];
                  return puntosDe(f).map((punto, idx) => {
                    if (punto.pagina !== p.indice) return null;
                    return (
                      <div
                        key={`${f.cliente_id}-${idx}`}
                        className={`absolute border-2 rounded flex items-center justify-center ${color.borde} ${color.fondo}`}
                        style={{
                          left: `${punto.rect_rel[0] * 100}%`,
                          top: `${punto.rect_rel[1] * 100}%`,
                          width: `${(punto.rect_rel[2] - punto.rect_rel[0]) * 100}%`,
                          height: `${(punto.rect_rel[3] - punto.rect_rel[1]) * 100}%`,
                        }}
                        data-testid={`casilla-marcador-${f.cliente_id}-${idx}`}
                      >
                        <span className={`text-[10px] font-medium truncate px-1 ${color.texto}`}>
                          {f.nombre}{puntosDe(f).length > 1 ? ` (${idx + 1})` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onQuitar?.(f.cliente_id, idx); }}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-gray-300 text-gray-600 text-[11px] leading-none shadow-sm hover:bg-red-50 hover:text-red-600"
                          title="Quitar este punto de firma"
                          data-testid={`casilla-quitar-${f.cliente_id}-${idx}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  });
                })}
                <div className="absolute bottom-1 right-2 text-[10px] text-slate-400">
                  {p.indice + 1} / {paginas.length}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {faltan.length > 0 ? (
            <span className="text-xs text-amber-700 mr-auto" data-testid="casillas-faltan">
              Falta colocar {faltan.length} casilla(s)
            </span>
          ) : (
            <span className="text-xs text-green-700 mr-auto" data-testid="casillas-total-puntos">
              {totalPuntos} punto(s) de firma colocado(s)
            </span>
          )}
          <Button onClick={() => onOpenChange(false)} disabled={faltan.length > 0}
                  data-testid="casillas-confirmar-btn">
            Usar estas posiciones
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColocarCasillasDialog;
